import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { getInitials } from "../../requests/utilities/helpers";

// Assign a task occurrence to specific people from the store roster. Saving
// an empty selection returns the task to everyone eligible.

const boxStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 460,
  maxWidth: "94vw",
  maxHeight: "82vh",
  display: "flex",
  flexDirection: "column",
  bgcolor: "background.paper",
  // Same chrome as the app's other dialogs (ConfirmDialog and the team modals).
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  p: 0,
  outline: "none",
};

const memberName = (m) =>
  (m.full_name || `${m.first_name || ""} ${m.last_name || ""}`).trim();

export default function AssignMemberModal({ open, task, slotLabel, members, selectedIds, roleNames, onClose, onSave }) {
  const [selected, setSelected] = React.useState(new Set());
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setSelected(new Set(selectedIds || []));
      setSearch("");
    }
  }, [open, selectedIds]);

  const roleName = (m) => {
    const roleId =
      m.primary_role_id ??
      (m.roles_and_role_types || []).find((r) => r.main_role_ind)?.role ??
      (m.roles_and_role_types || [])[0]?.role;
    return roleNames?.find((r) => r.id === roleId)?.role_name || "";
  };

  const list = (members || [])
    .filter((m) => (search ? memberName(m).toLowerCase().includes(search.toLowerCase()) : true))
    .sort((a, b) => memberName(a).localeCompare(memberName(b)));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <Modal open={!!open} onClose={onClose}>
      <Box sx={boxStyle}>
        <div className="absolute right-2 top-2 text-gray-500 cursor-pointer" onClick={onClose}>
          <CloseIcon />
        </div>
        <div className="px-6 pt-5 pb-3 pr-12 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-900">
            Assign {task?.name ? `"${task.name}"` : "task"}
            {slotLabel ? ` · ${slotLabel} slot` : ""}
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Only the people you pick will see this task. Clear the selection to open it back up to everyone eligible.
          </p>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search people"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mt: 2 }}
            InputProps={{
              sx: {
                backgroundColor: "white",
                borderRadius: "8px",
                fontSize: "14px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
              },
            }}
          />
        </div>
        <div className="flex-1 overflow-auto px-2 py-2">
          {list.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              {members?.length ? "No one matches." : "Nobody on the roster is eligible for this task."}
            </div>
          ) : (
            list.map((m) => (
              <label
                key={m.id}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                <Checkbox
                  size="small"
                  checked={selected.has(m.id)}
                  onChange={() => toggle(m.id)}
                  sx={{ color: "#94a3b8", "&.Mui-checked": { color: "#6d28d9" }, p: 0.5 }}
                />
                <span className="bg-violet-100 text-violet-700 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold flex-none">
                  {getInitials(memberName(m))}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-800 truncate">{memberName(m)}</span>
                  {roleName(m) && <span className="block text-[11px] text-slate-500">{roleName(m)}</span>}
                </span>
              </label>
            ))
          )}
        </div>
        {/* Footer in the app's dialog style: helper text left, compact buttons right. */}
        <div className="flex items-center gap-2 px-6 py-4 border-t border-slate-100">
          <div className="flex-1 text-xs text-slate-500">
            {selected.size ? `${selected.size} selected` : "Nobody selected: anyone eligible can do it"}
          </div>
          <button
            className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium whitespace-nowrap"
            onClick={() => onSave([])}
          >
            Clear (anyone)
          </button>
          <button
            className="px-4 py-2 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium"
            onClick={() => onSave([...selected])}
          >
            Save
          </button>
        </div>
      </Box>
    </Modal>
  );
}
